/**
 * Custom storefront auth — the website client of the `chefkatscookies-api`
 * Vercel backend (api.chefkatscookies.com), the same backend the mobile app
 * uses. The store's native Shopify login is disabled in favour of this so that
 * accounts map consistently to orders by email.
 *
 * A Shopify storefront's `customer` Liquid object can only be populated by
 * Shopify's own session cookie, which the custom JWT cannot create. So this is a
 * purely client-side layer: a provider (Google / Apple) proves identity, the
 * backend find-or-creates the Shopify customer by email and returns our own
 * app-session JWT pair, and we keep that pair in localStorage and render the
 * signed-in state with JS. Orders are read from the backend; on sign-in we also
 * stamp the account email onto the cart so the resulting order is traceable to
 * the account in admin even if a different email is typed at checkout.
 *
 * Config below may be overridden by setting `window.CKC_AUTH_CONFIG` before this
 * script loads (the account snippets pass values from theme settings).
 */
(function () {
  // The account snippets render twice (header popover + mobile drawer), so this
  // classic script can be included twice; only the first run should set up the
  // single shared controller.
  if (window.CKCAuth) return;

  const ORIGIN = window.location.origin;

  const CONFIG = Object.assign(
    {
      apiBase: 'https://api.chefkatscookies.com',
      // Reuses the dedicated "Chef Kat's Cookies" Google Web client (public id —
      // the secret stays on the server). The redirectUri below must be added as
      // an Authorized redirect URI on that client AND set as GOOGLE_WEB_REDIRECT_URI
      // in the Vercel backend env.
      googleClientId:
        '100165783049-2k0rue74gfkijepnf8ikm7pa54g240if.apps.googleusercontent.com',
      // Apple "Services ID" for web (NOT the iOS bundle id). Create it in the
      // Apple Developer portal and set APPLE_WEB_SERVICE_ID on the backend to the
      // same value. While blank, the Apple button hides itself.
      appleServiceId: '',
      // Email-code sign-in (a code is emailed, then typed back). Toggled by the
      // "Email sign-in" theme setting; the backend must also have GMAIL_USER /
      // GMAIL_APP_PASSWORD set or /api/auth/email/start returns 503.
      emailLoginEnabled: false,
      // The storefront page that receives the OAuth redirect. Create a Page in
      // admin with the URL handle `auth-callback` using the `page.auth-callback`
      // template.
      redirectUri: ORIGIN + '/pages/auth-callback',
      // Cart attribute that records the signed-in account email on orders.
      cartEmailAttribute: 'Account Email',
      // Publishable Storefront API token, used to build a prefilled checkout for
      // signed-in customers (mirrors the cart into a Storefront cart stamped with
      // their email + saved address). Blank disables the prefill (caller falls
      // back to the normal checkout). The storefront API version below must be
      // one the store supports.
      storefrontToken: '',
      storefrontApiVersion: '2025-01',
    },
    window.CKC_AUTH_CONFIG || {}
  );

  const ACCESS_KEY = 'ckc.session.access';
  const REFRESH_KEY = 'ckc.session.refresh';
  const SS = {
    verifier: 'ckc.pkce.verifier',
    state: 'ckc.oauth.state',
    provider: 'ckc.oauth.provider',
    returnTo: 'ckc.oauth.return',
  };

  // ---- token storage ----
  const getAccess = () => localStorage.getItem(ACCESS_KEY);
  const getRefresh = () => localStorage.getItem(REFRESH_KEY);
  function saveTokens(t) {
    localStorage.setItem(ACCESS_KEY, t.accessToken);
    localStorage.setItem(REFRESH_KEY, t.refreshToken);
  }
  function clearTokens() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  }

  // ---- device-local profile (display name / preferred email) ----
  // Mirrors the mobile app: stored on the device only (not on the Shopify
  // customer), so they personalise the greeting but don't change the account's
  // real email. Kept here so the popover + drawer instances share one source.
  // Source of truth is the backend (synced across devices via a customer
  // metafield); localStorage is just a fast cache so the greeting renders before
  // the profile fetch returns.
  const PROFILE_KEY = 'ckc.profile';
  let profileCache = null;
  function getProfile() {
    if (profileCache) return profileCache;
    try {
      profileCache = JSON.parse(localStorage.getItem(PROFILE_KEY) || '{}') || {};
    } catch (e) {
      profileCache = {};
    }
    return profileCache;
  }
  function cacheProfile(p) {
    profileCache = {
      displayName: (p && p.displayName) || null,
      preferredEmail: (p && p.preferredEmail) || null,
    };
    try {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(profileCache));
    } catch (e) {
      /* ignore */
    }
    return profileCache;
  }
  // Optimistic local update, then persist to the backend. Resolves when saved;
  // throws on failure (the caller reverts/marks the error).
  async function setProfile(patch) {
    cacheProfile(Object.assign({}, getProfile(), patch || {}));
    emit();
    const res = await authedFetch('/api/customer/profile', {
      method: 'PATCH',
      body: JSON.stringify(patch || {}),
    });
    if (!res.ok) {
      let detail = '';
      try {
        detail = (await res.json()).error || '';
      } catch (e) {
        /* ignore */
      }
      throw new Error(detail || 'Could not save your changes');
    }
    const data = await res.json();
    if (data && data.profile) {
      cacheProfile(data.profile);
      emit();
    }
    return getProfile();
  }
  function clearProfile() {
    profileCache = null;
    localStorage.removeItem(PROFILE_KEY);
  }

  // ---- saved shipping address (the customer's real Shopify default address) ----
  // Hydrated from the profile GET on load; edited from the Settings screen. Kept
  // in memory only (it's account data, not a personalisation cache) and cleared
  // on sign-out.
  let addressCache = null;
  function getAddress() {
    return addressCache;
  }
  async function saveAddress(input) {
    const res = await authedFetch('/api/customer/address', {
      method: 'POST',
      body: JSON.stringify(input || {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not save your address');
    addressCache = data.address || null;
    return addressCache;
  }
  async function deleteAddress() {
    const res = await authedFetch('/api/customer/address', { method: 'DELETE' });
    if (!res.ok) {
      let detail = '';
      try {
        detail = (await res.json()).error || '';
      } catch (e) {
        /* ignore */
      }
      throw new Error(detail || 'Could not remove your address');
    }
    addressCache = null;
  }

  // ---- prefilled Storefront checkout ----
  // The online-store (Liquid) cart checks out anonymously — Shopify's hosted
  // checkout has no buyer identity to prefill. To prefill it for a signed-in
  // customer, we mirror the cart into a Storefront cart stamped with their email
  // + saved shipping address and hand back THAT cart's checkoutUrl. Returns null
  // whenever we can't/shouldn't (signed out, no saved address, no token, or any
  // error) so the caller falls back to the normal checkout untouched.
  async function storefrontGraphQL(query, variables) {
    const version = CONFIG.storefrontApiVersion || '2025-01';
    const res = await fetch(window.location.origin + '/api/' + version + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': CONFIG.storefrontToken,
      },
      body: JSON.stringify({ query: query, variables: variables }),
    });
    const data = await res.json();
    if (data.errors) throw new Error('Storefront API error');
    return data.data;
  }

  async function createPrefilledCheckoutUrl(items, attributes, discountCodes) {
    try {
      if (!CONFIG.storefrontToken) return null;
      if (!currentUser || !currentUser.email) return null;
      const addr = addressCache;
      if (!addr || !(addr.address1 || addr.zip)) return null; // nothing useful to prefill
      if (!Array.isArray(items) || !items.length) return null;

      const lines = items.map((i) => ({
        merchandiseId: 'gid://shopify/ProductVariant/' + i.variantId,
        quantity: i.quantity,
      }));

      // Carry the online-store cart attributes verbatim (Delivery Date/Time,
      // Account Email, etc.) so the slot-booking webhook still sees them.
      const attrs = Object.keys(attributes || {})
        .filter((k) => attributes[k] != null && String(attributes[k]) !== '')
        .map((k) => ({ key: k, value: String(attributes[k]) }));

      // deliveryAddressPreferences wants display NAMES (province/country).
      const deliveryAddress = {};
      if (addr.firstName) deliveryAddress.firstName = addr.firstName;
      if (addr.lastName) deliveryAddress.lastName = addr.lastName;
      if (addr.address1) deliveryAddress.address1 = addr.address1;
      if (addr.address2) deliveryAddress.address2 = addr.address2;
      if (addr.city) deliveryAddress.city = addr.city;
      if (addr.province) deliveryAddress.province = addr.province;
      if (addr.zip) deliveryAddress.zip = addr.zip;
      if (addr.country) deliveryAddress.country = addr.country;
      if (addr.phone) deliveryAddress.phone = addr.phone;

      const buyerIdentity = { email: currentUser.email };
      if (Object.keys(deliveryAddress).length) {
        buyerIdentity.deliveryAddressPreferences = [{ deliveryAddress: deliveryAddress }];
      }
      if (addr.phone) buyerIdentity.phone = addr.phone;

      const input = { lines: lines, attributes: attrs, buyerIdentity: buyerIdentity };
      if (Array.isArray(discountCodes) && discountCodes.length) {
        input.discountCodes = discountCodes;
      }

      const data = await storefrontGraphQL(
        'mutation CreateCart($input: CartInput!) {' +
          '  cartCreate(input: $input) {' +
          '    cart { checkoutUrl }' +
          '    userErrors { field message }' +
          '  }' +
          '}',
        { input: input }
      );
      const result = data && data.cartCreate;
      if (!result || !result.cart || (result.userErrors && result.userErrors.length)) return null;
      return result.cart.checkoutUrl || null;
    } catch (e) {
      return null; // any failure → caller uses the normal checkout
    }
  }

  // Address autocomplete via the backend proxy (key stays server-side). Returns
  // [] on any error — manual entry always works.
  async function autocompleteAddress(text, country) {
    const q = new URLSearchParams({ text: text });
    if (country) q.set('country', country);
    try {
      const res = await authedFetch('/api/address/autocomplete?' + q.toString(), { method: 'GET' });
      if (!res.ok) return [];
      const data = await res.json().catch(() => ({}));
      return data.suggestions || [];
    } catch (e) {
      return [];
    }
  }

  // Settings "verify your email" flow — separate from the email-code LOGIN flow.
  // Sends a code to a candidate preferred email, then checks it WITHOUT logging
  // in (so changing the preferred email never swaps the signed-in account).
  let profileEmailChallenge = null;
  async function sendProfileEmailCode(email) {
    const res = await fetch(CONFIG.apiBase + '/api/auth/email/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email }),
    });
    if (!res.ok) {
      let detail = '';
      try {
        detail = (await res.json()).error || '';
      } catch (e) {
        /* ignore */
      }
      throw new Error(detail || 'Could not send a code');
    }
    profileEmailChallenge = (await res.json()).challenge;
  }
  async function verifyProfileEmailCode(code) {
    if (!profileEmailChallenge) throw new Error('Request a code first');
    const res = await fetch(CONFIG.apiBase + '/api/auth/email/check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ challenge: profileEmailChallenge, code: code }),
    });
    const data = await res.json().catch(() => ({}));
    return !!(res.ok && data.ok);
  }

  // ---- PKCE helpers (Web Crypto) ----
  function base64url(bytes) {
    let str = '';
    bytes.forEach((b) => (str += String.fromCharCode(b)));
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function randomString(byteLength) {
    return base64url(crypto.getRandomValues(new Uint8Array(byteLength)));
  }
  async function pkceChallenge(verifier) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return base64url(new Uint8Array(digest));
  }

  // ---- simple pub/sub for auth state ----
  const listeners = new Set();
  let currentUser = null; // AuthUser | null
  let ready = false; // becomes true after the first restore attempt
  function emit() {
    listeners.forEach((fn) => {
      try {
        fn(currentUser, ready);
      } catch (e) {
        /* listener errors shouldn't break others */
      }
    });
    // A sign-in/out can change the effective launch state (the preview account
    // unlocks the live UI), so re-notify rewards subscribers too.
    if (typeof emitConfig === 'function') emitConfig();
  }
  function onChange(fn) {
    listeners.add(fn);
    fn(currentUser, ready); // fire immediately with current state
    return () => listeners.delete(fn);
  }

  // ---- Kat's Rewards launch flag (public config) ----
  // The whole rewards program hides behind ONE server flag: `REWARDS_LAUNCHED`
  // on the chefkatscookies-api backend, surfaced by the PUBLIC (no-auth)
  // `GET /api/rewards/config` endpoint. Until it loads (or if it fails) we treat
  // rewards as NOT launched ("coming soon") so nothing half-built ever shows.
  //
  // This is a SEPARATE switch from the `settings.rewards_enabled` theme setting:
  //   - `settings.rewards_enabled` = hard master kill-switch. When OFF, the whole
  //     program is hidden server-side regardless of this flag (the Liquid gates
  //     never render the rewards surfaces at all).
  //   - `launched` (this flag) = the LAUNCH action. With the master switch ON,
  //     `launched === false` shows "coming soon" everywhere and `true` shows the
  //     live balance / ladder / redeem UI. Flipping it is a server change (env
  //     var), so no theme redeploy is needed to go live.
  const configListeners = new Set();
  let launched = false; // safe default: coming-soon until the server says otherwise
  let rewardsConfig = null; // full { launched, earn, ladder, activeMultiplier } once fetched
  let configLoaded = false;

  // Preview override: this one signed-in account sees the live rewards UI even
  // while `launched` is false (the public still sees "coming soon"), so Kat can
  // test the program pre-launch. Pairs with the accrual allowlist in
  // slot-booking-api (only this account earns points until REWARDS_LAUNCHED is
  // true). Effective launch = server flag OR the preview account is signed in.
  const PREVIEW_EMAIL = 'chefkat@chefkatscookies.com';
  function isPreviewUser() {
    return !!(currentUser && currentUser.email && currentUser.email.toLowerCase() === PREVIEW_EMAIL);
  }
  function effectiveLaunched() {
    return launched || isPreviewUser();
  }
  function emitConfig() {
    CONFIG.launched = effectiveLaunched();
    configListeners.forEach((fn) => {
      try {
        fn(effectiveLaunched(), rewardsConfig);
      } catch (e) {
        /* listener errors shouldn't break others */
      }
    });
  }
  // Subscribe to the launch flag. Fires immediately with the current (effective)
  // value (default false) and again once the fetch resolves or the signed-in
  // user changes, so callers render coming-soon first and reveal the live UI
  // only if/when launched (or the preview account signs in).
  function onRewardsConfig(fn) {
    configListeners.add(fn);
    fn(effectiveLaunched(), rewardsConfig);
    return () => configListeners.delete(fn);
  }
  async function loadRewardsConfig() {
    try {
      const res = await fetch(CONFIG.apiBase + '/api/rewards/config', { method: 'GET' });
      if (res.ok) {
        rewardsConfig = await res.json();
        launched = !!(rewardsConfig && rewardsConfig.launched);
      }
      // A non-ok response leaves `launched` at its safe default (false).
    } catch (e) {
      /* network error → treat as not launched (coming soon) */
    }
    configLoaded = true;
    CONFIG.launched = launched; // mirror onto the config object for convenience
    emitConfig();
    return launched;
  }

  // ---- backend calls ----
  async function refreshSession() {
    const refreshToken = getRefresh();
    if (!refreshToken) return false;
    try {
      const res = await fetch(CONFIG.apiBase + '/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        clearTokens();
        return false;
      }
      saveTokens(await res.json());
      return true;
    } catch (e) {
      return false;
    }
  }

  // fetch against the backend with the access token attached, rotating the
  // refresh token once on a 401.
  async function authedFetch(path, init) {
    init = init || {};
    const doFetch = () =>
      fetch(CONFIG.apiBase + path, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          ...(init.headers || {}),
          Authorization: 'Bearer ' + (getAccess() || ''),
        },
      });
    let res = await doFetch();
    if (res.status === 401 && (await refreshSession())) {
      res = await doFetch();
    }
    return res;
  }

  // Exchanges a verified provider token for our session pair + profile.
  async function exchange(path, body) {
    const res = await fetch(CONFIG.apiBase + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let detail = '';
      try {
        detail = (await res.json()).error || '';
      } catch (e) {
        /* ignore */
      }
      throw new Error(detail || 'Sign-in failed (' + res.status + ')');
    }
    const data = await res.json();
    saveTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    return data.user;
  }

  // ---- checkout email linking ----
  // Stamps the account email onto the cart so the order is traceable to the
  // account in admin (find-or-create canonicalises on email). Best-effort.
  async function stampCartEmail(email) {
    try {
      const attributes = {};
      attributes[CONFIG.cartEmailAttribute] = email || '';
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });
    } catch (e) {
      /* non-fatal */
    }
  }

  // ---- session lifecycle ----
  async function loadCurrentUser() {
    if (!getAccess()) {
      currentUser = null;
      ready = true;
      emit();
      return null;
    }
    try {
      const res = await authedFetch('/api/customer/profile', { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        currentUser = data.user;
        if (data.profile) cacheProfile(data.profile); // synced display name / preferred email
        addressCache = data.address || null; // saved shipping address (for Settings)
      } else {
        currentUser = null;
      }
    } catch (e) {
      currentUser = null;
    }
    ready = true;
    emit();
    return currentUser;
  }

  function signOut() {
    clearTokens();
    clearProfile(); // don't leak a display name to the next person on this browser
    addressCache = null; // don't leak the saved address either
    currentUser = null;
    ready = true;
    stampCartEmail('');
    emit();
  }

  async function getOrders() {
    const res = await authedFetch('/api/customer/orders', { method: 'GET' });
    if (!res.ok) throw new Error('Could not load orders');
    return (await res.json()).orders || [];
  }

  // ---- Kat's Rewards (Paw Points) ----
  // Same Bearer pattern as orders/profile; the ledger + ladder come from the
  // server (chefkatscookies-api /api/rewards/*), so nothing is hardcoded here.
  async function getRewardsSummary() {
    const res = await authedFetch('/api/rewards/summary', { method: 'GET' });
    if (!res.ok) throw new Error('Could not load rewards');
    return res.json(); // { balance, lifetime, ladder, activity, vouchers }
  }
  // Redeems a rung. `requestId` makes a retried call idempotent (same code back,
  // no double deduction) — callers should reuse it across retries of one intent.
  async function redeemReward(rung, requestId) {
    const res = await authedFetch('/api/rewards/redeem', {
      method: 'POST',
      body: JSON.stringify({ rung: rung, requestId: requestId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not redeem this reward');
    return data; // { voucher: { code, description, expiresAt }, summary }
  }

  // Deletes the account (server hard-deletes if possible, else files a request),
  // then clears the local session. Resolves to 'deleted' | 'requested'.
  async function deleteAccount() {
    const res = await authedFetch('/api/customer/delete', { method: 'POST' });
    if (!res.ok) {
      let detail = '';
      try {
        detail = (await res.json()).error || '';
      } catch (e) {
        /* ignore */
      }
      throw new Error(detail || 'Could not delete your account');
    }
    const status = (await res.json()).status;
    signOut();
    return status;
  }

  // ---- email-code flow ----
  // No popup/OAuth: the backend emails a one-time code and returns an opaque
  // short-lived `challenge` (held in memory here); the user types the code, which
  // we send back with the challenge to prove the email. Identity then maps to a
  // Shopify customer the same way the OAuth providers do.
  let emailChallenge = null;

  async function requestEmailCode(email) {
    const res = await fetch(CONFIG.apiBase + '/api/auth/email/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email }),
    });
    if (!res.ok) {
      let detail = '';
      try {
        detail = (await res.json()).error || '';
      } catch (e) {
        /* ignore */
      }
      throw new Error(detail || 'Could not send a code (' + res.status + ')');
    }
    emailChallenge = (await res.json()).challenge;
  }

  async function verifyEmailCode(code) {
    if (!emailChallenge) throw new Error('Request a code first');
    const user = await exchange('/api/auth/email/verify', {
      challenge: emailChallenge,
      code: code,
    });
    emailChallenge = null;
    currentUser = user;
    ready = true;
    await stampCartEmail(user && user.email);
    emit();
    loadCurrentUser(); // hydrate synced profile (display name / preferred email) in the background
    return user;
  }

  // ---- provider flows ----

  // Resolves when the OAuth popup signals success via postMessage; rejects if it
  // reports an error or the user closes it before finishing.
  function awaitPopup(popup) {
    return new Promise((resolve, reject) => {
      function cleanup() {
        window.removeEventListener('message', onMessage);
        clearInterval(timer);
      }
      function onMessage(e) {
        if (e.origin !== ORIGIN || !e.data || e.data.type !== 'ckc-auth') return;
        cleanup();
        if (e.data.ok) {
          // The popup closes itself after showing "Login successful!"; closing
          // here too is a harmless no-op if it's already gone.
          try {
            popup.close();
          } catch (err) {
            /* ignore */
          }
          resolve();
        } else {
          // Leave the popup open so the user can read the error and close it.
          reject(new Error(e.data.error || 'Sign-in failed'));
        }
      }
      window.addEventListener('message', onMessage);
      const timer = setInterval(() => {
        if (popup.closed) {
          cleanup();
          reject(new Error('Sign-in was cancelled'));
        }
      }, 500);
    });
  }

  // Google: authorization-code + PKCE. We store the PKCE verifier + state, then
  // open Google in a POPUP so the page (and the open account menu) stays put. The
  // callback page does the exchange and posts the result back here; we then just
  // refresh the session in place. If the popup is blocked, we fall back to a
  // full-page redirect (the callback page redirects back).
  async function signInWithGoogle() {
    // Open the popup synchronously — BEFORE the await below — so the browser
    // treats it as user-initiated and doesn't block it. We point it at the auth
    // URL once the PKCE challenge (async) is ready.
    const popup = window.open('', 'ckc-auth', 'popup,width=480,height=680');

    const verifier = randomString(48);
    const state = randomString(16);
    localStorage.setItem(SS.verifier, verifier);
    localStorage.setItem(SS.state, state);
    localStorage.setItem(SS.provider, 'google');
    localStorage.setItem(SS.returnTo, window.location.href);
    const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    url.search = new URLSearchParams({
      client_id: CONFIG.googleClientId,
      redirect_uri: CONFIG.redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      code_challenge: await pkceChallenge(verifier),
      code_challenge_method: 'S256',
      state: state,
      prompt: 'select_account',
    }).toString();
    const authUrl = url.toString();

    if (!popup) {
      // Popup blocked — fall back to a full-page redirect (callback redirects back).
      window.location.href = authUrl;
      return;
    }
    popup.location.href = authUrl;
    await awaitPopup(popup);
    await loadCurrentUser();
  }

  function clearOauthState() {
    [SS.verifier, SS.state, SS.provider, SS.returnTo].forEach((k) => localStorage.removeItem(k));
  }

  function b64urlDecode(s) {
    s = s.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    return atob(s);
  }

  // Completes sign-in on the callback page. Two shapes:
  //  - Google: an authorization `?code=` we exchange with the backend.
  //  - Apple (web): the backend already minted the session and handed it back in
  //    the URL fragment (`#ckc=<tokens>&state=`), because Apple is verified
  //    against the api domain, not the storefront.
  // Returns the URL to send the user back to.
  async function handleCallback() {
    const returnTo = localStorage.getItem(SS.returnTo) || '/';

    // --- Fragment handoff (Apple web) ---
    const hash = window.location.hash.replace(/^#/, '');
    if (hash) {
      const hp = new URLSearchParams(hash);
      if (hp.get('error')) {
        clearOauthState();
        throw new Error(hp.get('error'));
      }
      if (hp.get('ckc')) {
        if (hp.get('state') !== localStorage.getItem(SS.state)) {
          throw new Error('Sign-in failed: state mismatch');
        }
        saveTokens(JSON.parse(b64urlDecode(hp.get('ckc'))));
        clearOauthState();
        // Strip the tokens out of the address bar / history.
        history.replaceState(null, '', window.location.pathname);
        ready = true;
        emit();
        return returnTo;
      }
    }

    // --- Authorization code (Google) ---
    const params = new URLSearchParams(window.location.search);
    const provider = localStorage.getItem(SS.provider);
    if (params.get('error')) throw new Error('Sign-in was cancelled');
    if (provider !== 'google') throw new Error('Unexpected sign-in callback');
    const code = params.get('code');
    if (!code) throw new Error('No authorization code returned');
    if (params.get('state') !== localStorage.getItem(SS.state)) {
      throw new Error('Sign-in failed: state mismatch');
    }
    const verifier = localStorage.getItem(SS.verifier);
    const user = await exchange('/api/auth/google', {
      code,
      codeVerifier: verifier || '',
      redirectUri: CONFIG.redirectUri,
    });
    clearOauthState();
    currentUser = user;
    ready = true;
    await stampCartEmail(user && user.email);
    emit();
    return returnTo;
  }

  // Apple (web): authorization with form_post. Apple can't verify the storefront
  // domain (Shopify won't host the /.well-known association file), so the OAuth
  // redirect_uri is the backend (/api/auth/apple-web) on the api domain, which IS
  // verified. That endpoint mints our session and bounces the popup back to this
  // storefront's callback page with the tokens in the fragment (handled above in
  // handleCallback). Mirrors the Google popup otherwise.
  async function signInWithApple() {
    if (!CONFIG.appleServiceId) throw new Error('Apple sign-in is not configured');
    // Open the popup synchronously (user gesture) before any async work.
    const popup = window.open('', 'ckc-auth', 'popup,width=480,height=680');

    const state = randomString(16);
    localStorage.setItem(SS.state, state);
    localStorage.setItem(SS.provider, 'apple');
    localStorage.setItem(SS.returnTo, window.location.href);
    const url = new URL('https://appleid.apple.com/auth/authorize');
    url.search = new URLSearchParams({
      client_id: CONFIG.appleServiceId,
      redirect_uri: CONFIG.apiBase + '/api/auth/apple-web',
      response_type: 'code id_token',
      response_mode: 'form_post',
      scope: 'name email',
      state: state,
    }).toString();
    const authUrl = url.toString();

    if (!popup) {
      window.location.href = authUrl;
      return;
    }
    popup.location.href = authUrl;
    await awaitPopup(popup);
    await loadCurrentUser();
    if (currentUser) await stampCartEmail(currentUser.email);
  }

  // ---- public surface ----
  window.CKCAuth = {
    config: CONFIG,
    onChange,
    loadCurrentUser,
    getOrders,
    getRewardsSummary,
    redeemReward,
    // Launch flag (see loadRewardsConfig above): `launched` is the live value,
    // `onRewardsConfig` subscribes to it, `getRewardsConfig` returns the raw
    // public config payload (earn rates / ladder / active multiplier) or null.
    onRewardsConfig,
    getRewardsConfig: () => rewardsConfig,
    signInWithGoogle,
    signInWithApple,
    requestEmailCode,
    verifyEmailCode,
    handleCallback,
    signOut,
    getProfile,
    setProfile,
    sendProfileEmailCode,
    verifyProfileEmailCode,
    getAddress,
    saveAddress,
    deleteAddress,
    autocompleteAddress,
    createPrefilledCheckoutUrl,
    deleteAccount,
    get user() {
      return currentUser;
    },
    appleEnabled: () => !!CONFIG.appleServiceId,
    emailEnabled: () => !!CONFIG.emailLoginEnabled,
    get launched() {
      return effectiveLaunched();
    },
    get configLoaded() {
      return configLoaded;
    },
  };

  // Fetch the public launch config early (independent of the auth session, since
  // it gates the pre-login coming-soon UI too). Best-effort: on any failure the
  // flag stays false and everything shows "coming soon".
  loadRewardsConfig();

  // Restore session on load, UNLESS the dedicated callback section is actually on
  // the page (it drives its own handleCallback flow and we'd otherwise race it).
  // We detect it by the section's container rather than the URL, so a missing or
  // misconfigured /pages/auth-callback page can't leave the account menu stuck in
  // a "loading" state — it just falls through to a normal signed-out restore.
  if (!document.querySelector('[data-ckc-auth-callback]')) {
    loadCurrentUser();
  }
})();
